import { useState } from 'react';

import { AlertCircle, Save } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import ReactJson from '@microlink/react-json-view';

interface ConfigurationViewerProps {
  config: Record<string, any> | null;
  client: string;
  environment: string;
  onSave?: (updatedConfig: Record<string, any>) => Promise<void>;
}

export const ConfigurationViewer = ({
  config,
  client,
  environment,
  onSave,
}: ConfigurationViewerProps) => {
  const [editedConfig, setEditedConfig] = useState<Record<string, any> | null>(config);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Check if config is empty or null
  const hasConfig = config && Object.keys(config).length > 0;

  const handleEdit = (edit: any) => {
    // Update edited config when user makes changes
    setEditedConfig(edit.updated_src);
    setHasChanges(true);
    setSaveError(null);
  };

  const handleAdd = (add: any) => {
    // Update edited config when user adds fields
    setEditedConfig(add.updated_src);
    setHasChanges(true);
    setSaveError(null);
  };

  const handleDelete = (del: any) => {
    // Update edited config when user deletes fields
    setEditedConfig(del.updated_src);
    setHasChanges(true);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editedConfig || !onSave) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await onSave(editedConfig);
      setHasChanges(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedConfig(config);
    setHasChanges(false);
    setSaveError(null);
  };

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-semibold'>Environment Configuration</h3>
          <p className='text-sm text-muted-foreground'>
            {client} • {environment}
          </p>
        </div>

        {/* Save Button - Only show if onSave is provided and there are changes */}
        {hasConfig && onSave && hasChanges && (
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='sm' onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant='default' size='sm' onClick={handleSave} disabled={isSaving}>
              <Save className='h-4 w-4 mr-1' />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      <Separator />

      {/* Save Error */}
      {saveError && (
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      {/* Config Display/Editor */}
      {!hasConfig ? (
        <div className='rounded-lg border border-dashed p-8 text-center'>
          <p className='text-muted-foreground'>No configuration available for this environment</p>
        </div>
      ) : (
        <div className='rounded-lg border bg-card max-h-[600px] overflow-y-auto'>
          <div className='p-4'>
            <ReactJson
              src={editedConfig || config}
              theme='rjv-default'
              iconStyle='triangle'
              displayDataTypes={false}
              displayObjectSize={true}
              enableClipboard={true}
              collapsed={2}
              // Enable editing only if onSave is provided
              onEdit={onSave ? handleEdit : false}
              onAdd={onSave ? handleAdd : false}
              onDelete={onSave ? handleDelete : false}
              style={{
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontFamily: 'ui-monospace, monospace',
              }}
            />
          </div>
        </div>
      )}

      {/* Footer Info */}
      {hasConfig && (
        <div className='flex items-center justify-between text-xs text-muted-foreground'>
          <span>
            {Object.keys(config).length} top-level key{Object.keys(config).length !== 1 ? 's' : ''}
          </span>
          {onSave ? (
            <span className='text-blue-600'>
              Click on values to edit • Click + to add • Click - to delete
            </span>
          ) : (
            <span>Read-only view</span>
          )}
        </div>
      )}
    </div>
  );
};
